import { Button, Col, Modal, ModalBody, Row, Spinner } from "reactstrap";
import { Icon, RSelect } from "../../../components/Component";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { validate } from "../utils";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { updateCommunityTeam } from "../../../redux/actions/community-teams";
import { SPECIAL_TEAMS } from "../constants";
import { fetchCommunitiesList } from "../../../redux/actions/property";
import { fetchAgentsList } from "../../../redux/actions/user";
import ChannelPartnersFilter from "../../components/filter-components/ChannelPartnersFilter";

const TeamEditModal = ({
  communityTeam,
  isOpen,
  toggleFn,
  refreshFn,
  viewType,
}) => {
  const axios = useAxiosPrivate();
  const dispatch = useDispatch();
  const isChannelPartnerView = viewType === "channelPartnerTeams";
  const modalTitle = isChannelPartnerView ? "Edit Channel Partner Team" : "Edit Master Community";
  const teamNameLabel = isChannelPartnerView ? "Channel Partner Team Name *" : "Master Community Name *";

  const [selectedChannelPartnerFilter, setSelectedChannelPartnerFilter] = useState(null);
  const [memberOptions, setMemberOptions] = useState([]);
  const [communityOptions, setCommunityOptions] = useState([]);
  const [selectedTeamName, setSelectedTeamName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedCommunities, setSelectedCommunities] = useState([]);

  const { loading, error } = useSelector((state) => state.communityTeam);

  const { communities, communitiesLoading } = useSelector((state) => ({
    communities: state.property?.communities,
    communitiesLoading: state.property?.communitiesLoading,
  }));

  const { agents, agentsLoading } = useSelector((state) => ({
    agents: state.user?.agents,
    agentsLoading: state.user?.loading,
  }));

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCommunitiesList(axios));
      dispatch(fetchAgentsList(axios));
    }
  }, [axios, dispatch, isOpen]);

  useEffect(() => {
    if (communities?.length) {
      setCommunityOptions(
        communities
          ?.filter((c) => !c?.team_id || c.team_id === communityTeam?.id)
          ?.map((c) => ({
            ...c,
            label: c?.community,
            value: c?.id ?? c?._id,
          })) ?? []
      );
    } else {
      setCommunityOptions([]);
    }
    if (agents?.length) {
      setMemberOptions(
        agents?.map((agent) => ({
          ...agent,
          label: `${agent?.user?.first_name} ${agent?.user?.last_name}`,
          value: agent?.userId,
        })) ?? []
      );
    } else {
      setMemberOptions([]);
    }
  }, [agents, communities, communityTeam?.id, isOpen]);

  useEffect(() => {
    if (isOpen && communityTeam) {
      setSelectedTeamName(communityTeam?.teamName ?? "");
      setSelectedMembers(
        communityTeam?.members?.map((m) => {
          const value = m?.id ?? m?._id ?? m?.userId;
          const existingOption = memberOptions.find(opt => opt.value === value);
          return existingOption || { label: `${m?.first_name} ${m?.last_name ?? ""}`, value, ...m };
        }) ?? []
      );
      setSelectedCommunities(
        communityTeam?.communities?.map((c) => {
          const value = c?.id ?? c?._id;
          const existingOption = communityOptions.find(opt => opt.value === value);
          return existingOption || { label: c?.community, value, ...c };
        }) ?? []
      );

      if (isChannelPartnerView && communityTeam?.partner) {
        if (typeof communityTeam.partner === 'object' && communityTeam.partner.id && communityTeam.partner.name) {
          setSelectedChannelPartnerFilter({ value: communityTeam.partner.id, label: communityTeam.partner.name });
        } else if (typeof communityTeam.partner === 'string') {
          // This might require ChannelPartnersFilter's options to be available
          // For now, setting value and a placeholder label.
          setSelectedChannelPartnerFilter({ value: communityTeam.partner, label: `Partner ID: ${communityTeam.partner}` });
        }
      } else {
        setSelectedChannelPartnerFilter(null);
      }

    } else if (!isOpen) {
      setSelectedTeamName("");
      setSelectedMembers([]);
      setSelectedCommunities([]);
      setSelectedChannelPartnerFilter(null);
    }
  }, [isOpen, communityTeam, isChannelPartnerView, memberOptions, communityOptions]);


  useEffect(() => {
    if (error && isOpen) {
      toast.error(error.message ?? `Error occurred while editing ${isChannelPartnerView ? "channel partner team" : "master community"}.`);
    }
  }, [error, isChannelPartnerView, isOpen]);

  const handleModalClose = (ev) => {
    if (ev) ev.preventDefault();
    toggleFn();
    setSelectedTeamName("");
    setSelectedMembers([]);
    setSelectedCommunities([]);
    setSelectedChannelPartnerFilter(null);
  };

  const handleEditFormSubmit = async (formEvent) => {
    formEvent.preventDefault();

    if (isChannelPartnerView && !selectedChannelPartnerFilter?.value) {
      toast.error("Please select a Channel Partner.");
      return;
    }

    const updatedMembers = selectedMembers.map((mem) => mem.value);
    const communitiesToValidate = isChannelPartnerView ? [] : selectedCommunities.map((com) => com.value);

    if (!validate(selectedTeamName, updatedMembers, communitiesToValidate, null, isChannelPartnerView)) {
      return;
    }

    const payload = {
      teamName: selectedTeamName,
      members: updatedMembers,
      communities: isChannelPartnerView ? [] : selectedCommunities.map((com) => com.value),
    };

    if (isChannelPartnerView) {
      payload.partner = selectedChannelPartnerFilter?.value;
    }

    const teamId = communityTeam?.id;

    dispatch(
      updateCommunityTeam(
        axios,
        teamId,
        payload,
        () => {
          handleModalClose();
        },
        () => {
          toast.success(`${isChannelPartnerView ? "Channel Partner Team" : "Master Community"} edited successfully!`);
          refreshFn();
        }
      )
    );
  };

  const handleMembersChange = (selectedOptions) => {
    setSelectedMembers(selectedOptions || []);
  };

  const handleCommunitiesChange = (selectedOptions) => {
    setSelectedCommunities(selectedOptions || []);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleModalClose}
      className="modal-dialog-centered"
      size="lg"
    >
      <ModalBody>
        <div className="nk-tnx-details mt-sm-3">
          <a href="#cancel" className="close" onClick={handleModalClose}>
            {" "}
            <Icon name="cross-sm" />
          </a>
          <div className="nk-modal-head mb-3">
            <h5 className="title">{modalTitle}</h5>
          </div>
        </div>
        <div className="p-2">
          <form onSubmit={handleEditFormSubmit}>
            <Row className="g-3">
              {isChannelPartnerView && (
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="channelPartner">
                      Channel Partner *
                    </label>
                    <ChannelPartnersFilter
                      stateVar={selectedChannelPartnerFilter}
                      onChange={(option) => setSelectedChannelPartnerFilter(option)}
                      isMulti={false}
                      className="customInput"
                    />
                  </div>
                </Col>
              )}
              <Col md={isChannelPartnerView && communityTeam?.teamName !== SPECIAL_TEAMS.OffPlan ? "12" : communityTeam?.teamName === SPECIAL_TEAMS.OffPlan ? "12" : "4"}>
                <div className="form-group">
                  <label className="form-label" htmlFor="teamName">
                    {teamNameLabel}
                  </label>
                  <div className="form-control-wrap">
                    <input
                      type="text"
                      className="form-control"
                      name="teamName"
                      required
                      value={selectedTeamName}
                      onChange={(e) => setSelectedTeamName(e.target.value)}
                    />
                  </div>
                </div>
              </Col>
              {communityTeam?.teamName !== SPECIAL_TEAMS.OffPlan && (
                <Col md={isChannelPartnerView ? "12" : "8"}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="teamMembers">
                      Agents *
                    </label>
                    <div className="form-control-wrap">
                      <RSelect
                        name="teamMembers"
                        placeholder="Select agents..."
                        isMulti
                        required
                        isLoading={agentsLoading}
                        loadingMessage={() => <Spinner size={'sm'} />}
                        value={selectedMembers}
                        options={memberOptions}
                        onChange={handleMembersChange}
                        className="customInput"
                      />
                    </div>
                  </div>
                </Col>
              )}
              {!isChannelPartnerView && communityTeam?.teamName !== SPECIAL_TEAMS.OffPlan && (
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="teamCommunities">
                      Places *
                    </label>
                    <div className="form-control-wrap">
                      <RSelect
                        name="teamCommunities"
                        isLoading={communitiesLoading}
                        loadingMessage={() => <Spinner size={'sm'} />}
                        placeholder="Select places..."
                        isMulti
                        value={selectedCommunities}
                        options={communityOptions}
                        onChange={handleCommunitiesChange}
                        className="customInput"
                      />
                    </div>
                  </div>
                </Col>
              )}
            </Row>
            <Col size="12" className="text-end mt-4">
              <Button color="primary" size="lg" disabled={loading || agentsLoading || communitiesLoading}>
                {loading ? "Saving..." : "Save"}
              </Button>
            </Col>
          </form>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default TeamEditModal;
